import React, { useMemo, useState, useCallback } from 'react';
import { DeliveryProvider, SystemConfig } from '../../types';
import { Truck, Copy, Check, Shield, Info, Webhook, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getFunctionsBaseUrl } from '../../services/supabase';

interface Props {
    config: SystemConfig;
    setConfig: (config: SystemConfig) => void;
    providers: DeliveryProvider[];
    storeId?: string;
}

const generateOpenDeliveryIdentity = () => {
    const merchantId = crypto.randomUUID();
    const merchantSecret = Array.from(crypto.getRandomValues(new Uint8Array(32)))
        .map(b => b.toString(16).padStart(2, '0')).join('');
    return { merchantId, merchantSecret };
};

export const IntegrationSubTabOpenDelivery: React.FC<Props> = ({ config, setConfig, providers, storeId }) => {
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);

    const identity = config.openDeliveryIdentity;
    const functionsBaseUrl = getFunctionsBaseUrl();
    const webhookEventsUrl = `${functionsBaseUrl}/sal-open-delivery-webhook/v1/newLogisticEvent`;
    const webhookCodeUrl = `${functionsBaseUrl}/sal-open-delivery-webhook/v1/confirmationCode`;

    const openDeliveryProviders = useMemo(
        () => (providers || []).filter(p => p.integrationType === 'open_delivery'),
        [providers]
    );

    const hasGlobalIdentity = Boolean(identity?.merchantId && identity?.merchantSecret);

    const copyToClipboard = (text: string, field: string) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        toast.success(`${field} copiado!`);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const handleGenerateIdentity = useCallback(async () => {
        if (isGenerating) return;
        setIsGenerating(true);
        
        try {
            const newIdentity = generateOpenDeliveryIdentity();
            await setConfig({ ...config, openDeliveryIdentity: newIdentity });
            toast.success('Identidade Open Delivery gerada com sucesso!');
        } catch (error) {
            console.error('[Open Delivery] Erro ao gerar identidade:', error);
            toast.error('Erro ao gerar identidade. Tente novamente.');
        } finally {
            setIsGenerating(false);
        }
    }, [config, setConfig, isGenerating]);

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">

            <div className="bg-card-bg rounded-3xl p-6 border border-border-dim space-y-4 shadow-xl">
                <div className="flex items-start gap-4">
                    <div className="p-3 bg-status-info/10 rounded-2xl border border-status-info/20 text-status-info shadow-inner">
                        <Shield className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-base font-black text-text-primary uppercase tracking-wider mb-1">Identidade da Loja (Global)</h3>
                        <p className="text-xs text-text-muted leading-relaxed font-medium">Estes são os dados de identificação do seu estabelecimento no ecossistema Open Delivery. Compartilhe-os com seus parceiros logísticos.</p>
                    </div>
                </div>

                {identity ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        {/* MERCHANT ID */}
                        <button type="button" className="w-full text-left bg-bg-main/50 p-4 rounded-2xl border border-border-dim group hover:border-status-info/30 transition-all cursor-pointer shadow-sm" onClick={() => copyToClipboard(identity.merchantId, 'Merchant ID')}>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-xs font-black text-text-muted uppercase tracking-widest">Merchant ID</span>
                                {copiedField === 'Merchant ID' ? <Check className="w-4 h-4 text-status-ready" /> : <Copy className="w-4 h-4 text-text-muted group-hover:text-status-info transition-colors" />}
                            </div>
                            <div className="font-mono text-sm text-status-info break-all select-all">{identity.merchantId}</div>
                        </button>

                        {/* MERCHANT SECRET */}
                        <button type="button" className="w-full text-left bg-bg-main/50 p-4 rounded-2xl border border-border-dim group hover:border-status-info/30 transition-all cursor-pointer shadow-sm" onClick={() => copyToClipboard(identity.merchantSecret, 'Secret')}>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-xs font-black text-text-muted uppercase tracking-widest">Merchant Secret</span>
                                {copiedField === 'Secret' ? <Check className="w-4 h-4 text-status-ready" /> : <Copy className="w-4 h-4 text-text-muted group-hover:text-status-info transition-colors" />}
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="font-mono text-sm text-status-info break-all select-all blur-[2px] hover:blur-none transition-all">{identity.merchantSecret}</div>
                            </div>
                        </button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="flex items-center gap-3 p-4 bg-status-pending/10 rounded-xl border border-status-pending/20 text-status-pending">
                            <AlertTriangle className="w-4 h-4 shrink-0" />
                            <p className="text-xs font-bold uppercase tracking-tight">Identidade global ainda não gerada. Clique no botão abaixo para gerar.</p>
                        </div>
                        <button
                            type="button"
                            onClick={handleGenerateIdentity}
                            disabled={isGenerating}
                            className="w-full py-3 px-4 bg-sal-cyber-teal text-white rounded-xl font-black uppercase text-xs tracking-widest hover:brightness-110 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isGenerating ? (
                                <><RefreshCw className="w-4 h-4 animate-spin" /> Gerando...</>
                            ) : (
                                <><Shield className="w-4 h-4" /> Gerar Identidade Agora</>
                            )}
                        </button>
                    </div>
                )}

                <div className="flex items-center gap-3 p-3 bg-status-pending/10 rounded-xl border border-status-pending/20 text-status-pending">
                    <Info className="w-4 h-4 shrink-0" />
                    <p className="text-xs font-bold uppercase tracking-tight">Configure a conexão específica de cada parceiro em: Configurações &gt; Apps Parceiros.</p>
                </div>
            </div>

            <div className="bg-card-bg rounded-3xl p-6 border border-border-dim space-y-4 shadow-xl">
                <div className="flex items-start gap-4">
                    <div className="p-3 bg-status-ready/10 rounded-2xl border border-status-ready/20 text-status-ready shadow-inner">
                        <Webhook className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-base font-black text-text-primary uppercase tracking-wider mb-1">Webhooks Open Delivery</h3>
                        <p className="text-xs text-text-muted leading-relaxed font-medium">Cadastre estas URLs no parceiro logístico para receber retorno de rastreio e código de confirmação.</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-3">
                    <button type="button" className="w-full text-left bg-bg-main/50 p-4 rounded-2xl border border-border-dim group hover:border-status-ready/30 transition-all cursor-pointer shadow-sm" onClick={() => copyToClipboard(webhookEventsUrl, 'Webhook Eventos')}>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-black text-text-muted uppercase tracking-widest">Tracking Events</span>
                            {copiedField === 'Webhook Eventos' ? <Check className="w-4 h-4 text-status-ready" /> : <Copy className="w-4 h-4 text-text-muted group-hover:text-status-ready transition-colors" />}
                        </div>
                        <div className="font-mono text-xs text-status-ready break-all select-all">{webhookEventsUrl}</div>
                    </button>

                    <button type="button" className="w-full text-left bg-bg-main/50 p-4 rounded-2xl border border-border-dim group hover:border-status-ready/30 transition-all cursor-pointer shadow-sm" onClick={() => copyToClipboard(webhookCodeUrl, 'Webhook Código')}>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-black text-text-muted uppercase tracking-widest">Confirmation Code</span>
                            {copiedField === 'Webhook Código' ? <Check className="w-4 h-4 text-status-ready" /> : <Copy className="w-4 h-4 text-text-muted group-hover:text-status-ready transition-colors" />}
                        </div>
                        <div className="font-mono text-xs text-status-ready break-all select-all">{webhookCodeUrl}</div>
                    </button>
                </div>

                <div className="flex items-center gap-3 p-3 bg-bg-main/40 rounded-xl border border-border-dim">
                    <Info className="w-4 h-4 shrink-0 text-text-muted" />
                    <p className="text-xs font-bold uppercase tracking-tight text-text-muted">Loja: <span className="text-text-primary">{storeId || 'N/D'}</span> • Identity: <span className={hasGlobalIdentity ? 'text-status-ready' : 'text-status-critical'}>{hasGlobalIdentity ? 'OK' : 'Pendente'}</span></p>
                </div>
            </div>

            <div className="bg-card-bg rounded-3xl p-6 border border-border-dim space-y-4 shadow-xl">
                <div className="flex items-start gap-4">
                    <div className="p-3 bg-status-info/10 rounded-2xl border border-status-info/20 text-status-info shadow-inner">
                        <Truck className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-base font-black text-text-primary uppercase tracking-wider mb-1">Saúde dos Parceiros</h3>
                        <p className="text-xs text-text-muted leading-relaxed font-medium">Checklist rápido para garantir cotação e despacho por API com fallback manual.</p>
                    </div>
                </div>

                {openDeliveryProviders.length === 0 ? (
                    <div className="flex items-center gap-3 p-4 bg-status-critical/10 rounded-xl border border-status-critical/20 text-status-critical">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        <p className="text-xs font-bold uppercase tracking-tight">Nenhum parceiro Open Delivery configurado em Apps Parceiros.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-3">
                        {openDeliveryProviders.map(provider => {
                            const od = provider.openDeliveryConfig;
                            const hasCredentials = Boolean(od?.clientId && od?.clientSecret);
                            const hasBaseUrl = Boolean(od?.baseUrl);
                            const isReady = provider.active && od?.active && hasCredentials && hasBaseUrl;
                            return (
                                <div key={provider.id} className="bg-bg-main/50 p-4 rounded-2xl border border-border-dim">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-black uppercase tracking-wider text-text-primary">{provider.name}</span>
                                        <span className={`text-[10px] px-2 py-1 rounded-full font-black uppercase tracking-widest ${isReady ? 'bg-status-ready/15 text-status-ready border border-status-ready/20' : 'bg-status-pending/15 text-status-pending border border-status-pending/20'}`}>
                                            {isReady ? 'Pronto' : 'Ajustar'}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px] font-bold uppercase tracking-tight">
                                        <div className={`flex items-center gap-2 ${hasCredentials ? 'text-status-ready' : 'text-status-critical'}`}>
                                            {hasCredentials ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} Credenciais
                                        </div>
                                        <div className={`flex items-center gap-2 ${hasBaseUrl ? 'text-status-ready' : 'text-status-critical'}`}>
                                            {hasBaseUrl ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} Base URL
                                        </div>
                                        <div className={`flex items-center gap-2 ${(provider.active && od?.active) ? 'text-status-ready' : 'text-status-pending'}`}>
                                            {(provider.active && od?.active) ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} Ativação
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

        </div>
    );
};
